import React from 'react';
import {
  MapPin,
  Star,
  CalendarBlank,
  Globe,
  InstagramLogo,
  Link as LinkIcon,
  Plus,
  Clock,
  Wine,
  SpinnerGap,
  GoogleLogo,
  Phone,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';

const RestaurantSidebar = ({
  restaurant,
  enriching,
  onShowPlanModal,
  onShowVisitModal,
  onAddToCalendar,
  onGoogleEnrich,
}) => {
  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="card-elevated card-padding space-y-3">
        {restaurant.state === 'want_to_try' && (
          <Button
            onClick={onShowPlanModal}
            className="w-full btn-secondary"
            data-testid="plan-visit-btn"
          >
            <CalendarBlank weight="duotone" className="w-5 h-5 mr-2" />
            Plan Visit
          </Button>
        )}

        {restaurant.state === 'planned' && restaurant.planned_visit_at && (
          <>
            <div className="text-center py-3 bg-[#D1E2C4]/30 rounded-lg">
              <p className="text-sm text-[#7A7873]">Planned for</p>
              <p className="font-semibold text-[#1A1918]">
                {new Date(restaurant.planned_visit_at).toLocaleDateString('en-ZA', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              {restaurant.booking_party_size && (
                <p className="text-sm text-[#7A7873] mt-1">
                  Party of {restaurant.booking_party_size}
                </p>
              )}
            </div>
            <Button
              onClick={onAddToCalendar}
              variant="outline"
              className="w-full"
              data-testid="calendar-btn"
            >
              <CalendarBlank weight="duotone" className="w-5 h-5 mr-2" />
              Add to Calendar
            </Button>
          </>
        )}

        <Button
          onClick={onShowVisitModal}
          className="w-full btn-primary"
          data-testid="log-visit-sidebar-btn"
        >
          <Plus weight="bold" className="w-5 h-5 mr-2" />
          Log Visit
        </Button>

        <Button
          onClick={onGoogleEnrich}
          disabled={enriching}
          variant="outline"
          className="w-full border-[#EAE8E3] hover:bg-[#F5F1EC] text-[#1A1918]"
          data-testid="google-enrich-btn"
        >
          {enriching ? (
            <SpinnerGap className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <GoogleLogo weight="bold" className="w-5 h-5 mr-2" />
          )}
          {enriching ? 'Looking up...' : 'Enrich from Google'}
        </Button>
      </div>

      {/* Links */}
      {(restaurant.booking_link || restaurant.website_url || restaurant.instagram_url) && (
        <div className="card-elevated card-padding space-y-3">
          <h3 className="font-semibold text-[#1A1918] mb-2">Links</h3>

          {restaurant.booking_link && (
            <a href={restaurant.booking_link} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#7A7873] hover:text-[#A94438] transition-colors">
              <LinkIcon weight="duotone" className="w-5 h-5" />
              <span>Make a booking</span>
            </a>
          )}

          {restaurant.website_url && (
            <a href={restaurant.website_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#7A7873] hover:text-[#A94438] transition-colors">
              <Globe weight="duotone" className="w-5 h-5" />
              <span>Website</span>
            </a>
          )}

          {restaurant.instagram_url && (
            <a href={restaurant.instagram_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#7A7873] hover:text-[#A94438] transition-colors">
              <InstagramLogo weight="duotone" className="w-5 h-5" />
              <span>Instagram</span>
            </a>
          )}
        </div>
      )}

      {/* Corkage */}
      {restaurant.corkage && (
        <div className="card-elevated card-padding">
          <div className="flex items-center gap-2 mb-2">
            <Wine weight="duotone" className="w-5 h-5 text-[#A94438]" />
            <h3 className="font-semibold text-[#1A1918]">Corkage</h3>
          </div>
          <p className="text-[#7A7873]">{restaurant.corkage}</p>
        </div>
      )}

      {/* Google data */}
      {(restaurant.google_rating || restaurant.phone || restaurant.opening_hours || restaurant.google_maps_url) && (
        <div className="card-elevated card-padding space-y-3" data-testid="google-data-section">
          <h3 className="font-semibold text-[#1A1918] flex items-center gap-2">
            <GoogleLogo weight="bold" className="w-4 h-4 text-[#4285F4]" />
            Google Info
          </h3>

          {restaurant.google_rating && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#7A7873]">Google Rating</span>
              <span className="flex items-center gap-1 font-semibold text-[#1A1918]">
                <Star weight="fill" className="w-4 h-4 text-[#FBBC04]" />
                {restaurant.google_rating}
                {restaurant.google_rating_count && (
                  <span className="text-xs text-[#9A9590] font-normal">({restaurant.google_rating_count})</span>
                )}
              </span>
            </div>
          )}

          {restaurant.phone && (
            <a href={`tel:${restaurant.phone}`}
              className="flex items-center gap-3 text-sm text-[#7A7873] hover:text-[#A94438] transition-colors">
              <Phone weight="duotone" className="w-4 h-4" />
              <span>{restaurant.phone}</span>
            </a>
          )}

          {restaurant.opening_hours && (
            <details className="text-sm">
              <summary className="flex items-center gap-3 text-[#7A7873] cursor-pointer hover:text-[#1A1918] transition-colors">
                <Clock weight="duotone" className="w-4 h-4" />
                Opening hours
              </summary>
              <pre className="mt-2 text-xs text-[#7A7873] whitespace-pre-wrap leading-relaxed pl-7">{restaurant.opening_hours}</pre>
            </details>
          )}

          {restaurant.google_maps_url && (
            <a href={restaurant.google_maps_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-3 text-sm text-[#7A7873] hover:text-[#A94438] transition-colors">
              <MapPin weight="duotone" className="w-4 h-4" />
              <span>View on Google Maps</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default RestaurantSidebar;
